import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, Disclosure } from "../mui";
import RichEditor from "../design/RichEditor";
import RichView from "../design/RichView";
import ProgressChoice from "../design/ProgressChoice";
import { MdAdd, MdErrorOutline, MdInfoOutline } from "react-icons/md";

/**
 * 工項詳情 — 工作區（照抄設計稿 el-pm-template_4.html:653-676）
 *
 * 一張卡兩塊：「要做什麼」（唯讀讀需求）與「我的回報」（唯一能寫字的地方）。
 * 舊版的快速回報是頁尾一條窄工具列，四個沒有標籤的小輸入格擠在一起，
 * 大字模式下整列會擠破；搬上來變成有標籤、有說明、有 44px 命中區的欄位。
 *
 * 整頁唯一的主要按鈕（contained）就在這裡（送出回報）——「回報進度」那顆已經降成
 * 次要按鈕，它的作用只是捲到這一區（硬規則 1）。
 */
function TaskWork({
  readOnly,
  busy,
  descHtml,
  descMeta,
  onSaveDesc,
  reviewerName,
  ownerHint,
  hideReport,
  qrPct,
  currentPct,
  onChangePct,
  qrNote,
  onChangeNote,
  qrStatus,
  onChangeStatus,
  statusOpts,
  qrHours,
  onChangeHours,
  onSubmit,
  onAddAttachment,
}) {
  /*
   * 需求說明預設是唯讀的（設計稿：這一塊是「讀」不是「寫」），但 PM 仍然要改得動 ——
   * 功能不准刪，所以留一顆文字入口切成編輯狀態，存檔後切回唯讀。
   */
  const [editing, setEditing] = useState(false);
  /* 「也要改狀態或登記工時」做成受控的，標頭右側的「展開／收合」字樣才知道該寫哪一個 */
  const [extraOpen, setExtraOpen] = useState(false);
  const draftRef = useRef(descHtml);

  const hasDesc = !!String(descHtml || "")
    .replace(/<[^>]*>/g, "")
    .trim() || /<img|<table/i.test(descHtml || "");

  const save = async () => {
    await onSaveDesc(draftRef.current);
    setEditing(false);
  };

  return (
    <Card component="section" sx={{ mt: 3 }}>
      <CardContent>
        <div data-tour="det-desc">
          <Typography variant="h3" component="h2">工項說明</Typography>

          {editing ? (
            <Box sx={{ mt: 1 }}>
              <RichEditor
                id="task-desc-editor"
                html={descHtml}
                placeholder="工作內容、目的與完成範圍…"
                onChange={(h) => {
                  draftRef.current = h;
                }}
              />
              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 1.5 }}>
                <Button variant="outlined" type="button" disabled={busy} loading={busy} loadingPosition="start" onClick={save}>
                  儲存
                </Button>
                <Button
                  variant="text"
                  type="button"
                  onClick={() => {
                    draftRef.current = descHtml;
                    setEditing(false);
                  }}
                >
                  取消
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              {hasDesc ? (
                <Box sx={{ mt: 1 }}>
                  {/* 需求裡常常貼截圖：RichView 會夾住圖寬，點圖還能放大看清楚 */}
                  <RichView
                    html={descHtml}
                    sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;"
                  />
                </Box>
              ) : (
                /* 空的區塊不要畫成一個空盒子，只留一句話與文字入口（硬規則 2） */
                <Typography sx={{ mt: 1 }}>尚無工項說明。</Typography>
              )}
              {/* 沒有內容就別寫「某某某某天寫的」—— 那會變成替一段不存在的文字掛名 */}
              {hasDesc && descMeta && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {descMeta}
                </Typography>
              )}
              {!readOnly && onSaveDesc && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text"
                    type="button"
                    startIcon={hasDesc ? undefined : <MdAdd aria-hidden="true" />}
                    onClick={() => {
                      draftRef.current = descHtml;
                      setEditing(true);
                    }}
                  >
                    {hasDesc ? "編輯說明" : "填寫說明"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </div>

        {/* 唯讀（審核）不提供任何會改資料的動作，整塊回報區不畫；
            審核人正要審這件工項時（hideReport）也不畫：他要做的是審核，不是回報 */}
        {!readOnly && !hideReport && (
          <div data-tour="det-quick">
            <Typography variant="h3" component="h2" sx={{ mt: 4, mb: 2 }}>
              我的回報
            </Typography>

            {/*
              誰回報得進去，由後端 MyTaskService.ResolveReportableTask 決定：
              指派人本人、開單的人、該專案的 Manager、以及資料範圍是「全部」的角色
              都放行，其餘會收到「這個工項不是指派給你的…」。
              先在表單之前講清楚，不要讓人填完一整段才被擋下來。
            */}
            {ownerHint && (
              <Banner kind="info" icon={<MdInfoOutline />} sx={{ mb: 2 }}>
                {ownerHint}
              </Banner>
            )}

            {/*
              拉到 100% 的後果要寫在表單「之前」，不是表單底下的灰色小字（硬規則 4）——
              這是送出之後才會發現的事。

              文案與設計稿一致，而且與後端行為一致：MyTaskService 的 UpdateProgress
              與 AddProgressRecord 在 progress >= 100 時會呼叫
              WorkflowService.TryTransitionToLegacyStatus 推到 InReview，
              再由 ReviewService.AutoOpenReview 自動開一張審核單指派把關者。
              後端自己的註解寫得很清楚：舊版「直接設成 Done」是一條繞過驗收條件與
              審核的後門，已經修掉了。

              後半句是審核之後的事：ReviewService.Decide 在審核通過時自動把工項推到
              「已完成」、要求修改時退回「進行中」，所以要講明「不用再按一次完成」——
              不講的話，大家會照舊習慣等核准之後回來找「完成」鈕，而那顆鈕已經不在了。
              說法與 /MyTask 的橫幅、resources/tours.js 的 my-submit 同一套，三處要一起改。
            */}
            <Banner kind="warn" icon={<MdErrorOutline />}>
              <strong>進度達 100% 時：　</strong>
              工項將自動送交{reviewerName}審核，送審後無法再編輯。
              審核通過後自動變更為「已完成」，無須另行操作；要求修改時退回「進行中」，修改完成後請重新送審。
            </Banner>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {/*
                與個人工項的回報卡用同一個元件。原本這裡是一格「只要填數字，例如 80」，
                個人工項那邊卻是五顆大按鈕 —— 同一件事在同一套系統裡兩種填法，
                換一個畫面就要重學一次。以個人工項那一版為準（見 ProgressChoice）。
              */}
              <ProgressChoice
                id="task-report-pct"
                label="目前進度"
                help={`填寫概略值即可，之後仍可調整。目前進度為 ${currentPct}%。`}
                value={qrPct}
                currentPct={currentPct}
                onChange={onChangePct}
              />

              <TextField
                id="task-report-note"
                label="本次工作內容"
                multiline
                minRows={3}
                fullWidth
                placeholder="簡述即可，例如：已修正鍵盤開啟時按鈕位置，尚待測試 iPad mini。"
                value={qrNote}
                onChange={(e) => onChangeNote(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />

              {/*
                狀態與工時在設計稿裡沒有位置，但它們是現行快速回報真的會寫進後端的兩個欄位
                （工時會累加到 actual_hours、狀態會走工作流轉換），不准刪 ——
                收進收合區，需要的人點開就有。
              */}
              <Disclosure
                title="變更狀態或登記工時"
                count="（2 項，選填）"
                open={extraOpen}
                onToggle={setExtraOpen}
                summaryExtra={extraOpen ? "收合" : "展開"}
              >
                <Stack spacing={3}>
                  <TextField
                    id="task-report-status"
                    select
                    fullWidth
                    label="變更狀態為"
                    helperText="無須變更時請保留「（不變更）」。"
                    value={qrStatus}
                    onChange={(e) => onChangeStatus(e.target.value)}
                    slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                  >
                    {statusOpts.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </TextField>
                  <TextField
                    placeholder="例如 1.5"
                    id="task-report-hours"
                    fullWidth
                    label="本次工時（小時）"
                    helperText="請填寫數字，將累加至該工項的實際工時。"
                    value={qrHours}
                    onChange={(e) => onChangeHours(e.target.value)}
                    slotProps={{ htmlInput: { inputMode: "decimal" } }}
                  />
                </Stack>
              </Disclosure>

              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="contained" type="button" disabled={busy} loading={busy} loadingPosition="start" onClick={onSubmit}>
                  送出回報
                </Button>
                <Button variant="text" type="button" disabled={busy} onClick={onAddAttachment} startIcon={<MdAdd aria-hidden="true" />}>
                  新增附件
                </Button>
              </Stack>
            </Stack>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

TaskWork.propTypes = {
  readOnly: PropTypes.bool,
  busy: PropTypes.bool,
  descHtml: PropTypes.string,
  /** 「{作者} {日期} 寫的」；取不到作者或日期就傳空字串，不要編 */
  descMeta: PropTypes.string,
  /** 傳 null 代表這個人不能改需求說明（唯讀模式） */
  onSaveDesc: PropTypes.func,
  reviewerName: PropTypes.string,
  /** 不是負責人本人時要先講清楚回報會被後端擋下；是本人就傳空字串 */
  ownerHint: PropTypes.string,
  /** 審核人正在審這件工項：回報區不畫 */
  hideReport: PropTypes.bool,
  qrPct: PropTypes.string,
  /** 這件工項目前的進度：value 為空字串（不動進度）時要顯示的就是它 */
  currentPct: PropTypes.number,
  onChangePct: PropTypes.func,
  qrNote: PropTypes.string,
  onChangeNote: PropTypes.func,
  qrStatus: PropTypes.string,
  onChangeStatus: PropTypes.func,
  statusOpts: PropTypes.array,
  qrHours: PropTypes.string,
  onChangeHours: PropTypes.func,
  onSubmit: PropTypes.func,
  onAddAttachment: PropTypes.func,
};

TaskWork.defaultProps = {
  readOnly: false,
  busy: false,
  descHtml: "",
  descMeta: "",
  onSaveDesc: null,
  reviewerName: "審核人",
  ownerHint: "",
  hideReport: false,
  qrPct: "",
  currentPct: 0,
  onChangePct: () => {},
  qrNote: "",
  onChangeNote: () => {},
  qrStatus: "",
  onChangeStatus: () => {},
  statusOpts: [],
  qrHours: "",
  onChangeHours: () => {},
  onSubmit: () => {},
  onAddAttachment: () => {},
};

export default TaskWork;
